import styled from "styled-components";
import dayjs from "dayjs";

import { Box, Grid, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";

import device from "src/styles/device";
import { Black, GreyStroke, White01 } from "src/styles/color";
import { useAuth } from "src/hooks/useAuth";
import { useDialog } from "src/context/DialogContext";
import { convertIntToCurrency, convertPrice } from "src/common";
import { lalamoveStatus, serviceType } from "src/common/lalamove";

import Button from "src/components/core/Button";
import Content from "src/components/ui/Content";
import Panel from "src/components/ui/Panel";
import InputText from "src/components/ui/InputText";
// import TextInputIcon from "src/components/ui/TextInputIcon";

import {
  Desc,
  Line,
  Section,
  TextSubTable,
  Title,
} from "src/modules/order/shipping-order/components";
import CustomerInfo from "src/modules/order/shipping-order/status-order/CustomerInfo";
import PurchaseInfo from "src/modules/order/shipping-order/status-order/PurchaseInfo";
import DocumentInfo from "src/modules/order/shipping-order/status-order/Document";
import Note from "src/modules/order/shipping-order/status-order/Note";
import LocationDialog from "src/modules/order/shipping-order/modal/LocaltionDialog";
import DeliveryProofImgDialog from "src/modules/order/shipping-order/modal/DeliveryProofImgDialog";
import PreviewImageList from "src/modules/order/shipping-order/modal/PreviewImageList";

const Price = styled.p`
  font-size: ${({ small }) => (small ? "1rem" : "1.5rem")};
  color: #e13b30;
  font-weight: 600;
  margin: 0 5px 0 0;
`;

const PaymentImage = styled.img`
  width: 300px;
  height: 355px;
  position: relative;
  object-fit: contain;
`;

const WrapperDeliveryType = styled.div`
  width: 100%;
  padding: 12px 16px;
  background: ${White01};
  border: 1px solid ${GreyStroke};
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  @media ${device.mobileL} {
    padding: 8px;
  }
`;

const SpanDeliverType = styled.p`
  margin: 0;
  color: ${Black};
  font-size: 1rem;
  font-weight: ${({ price }) => (price ? 500 : 400)};
  line-height: 24px;
`;

const SpanTel = styled.a`
  margin: 0;
  color: ${Black};
  font-size: 1rem;
  font-weight: 400;
  line-height: 24px;
  text-decoration: underline;
`;

const DateField = styled(TextField)(() => ({
  ".MuiInputBase-input": {
    color: "#252525",

    "&.Mui-disabled": {
      "-webkit-text-fill-color": "#252525",
    },
  },
  ".MuiInputBase-root": {
    height: "48px",
    "&.Mui-disabled": {
      background: "#F3F1F2",
      cursor: "no-drop",
    },
  },
  width: "100%",
}));

const ShippingDetail = ({ info, setIsLoading, callBackFetchInfo }) => {
  const { user, checkAllowPermission } = useAuth();
  const { openDialog, closeDialog, closeAllDialogs } = useDialog();

  const isAdmin = user?.role_id === 1;

  const isJSONString = (str) => {
    if (typeof str !== "string") return str;
    try {
      return isJSONString(str);
    } catch {
      return str;
    }
  };

  return (
    <Grid container xs={12}>
      <Grid container xs={12} sx={{ mb: { sm: 2, md: 3 } }}>
        <Grid item xs={6}>
          <Title>รายละเอียดการจัดส่งสินค้า</Title>
        </Grid>
      </Grid>

      <Section
        borderBottom={
          (info?.fulfillmentProcess?.[0]?.type !== "ไม่มีค่าจัดส่ง" ||
            info?.fulfillmentProcess?.[1]?.type === "มีค่าจัดส่ง") &&
          info?.method !== "ลูกค้ารับเอง"
        }
      >
        <Grid item xs={12} sm={6} sx={{ pr: { xs: 0, sm: 1.5 } }}>
          <Grid item xs={12} mb={0.5}>
            <Desc>การจัดส่ง</Desc>
          </Grid>

          <Grid item xs={12}>
            <InputText
              disabled
              value={
                info?.status?.value === "failed"
                  ? "สินค้าไม่พร้อมจัดส่ง"
                  : "มีสินค้าพร้อมจัดส่ง"
              }
            />
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          sx={{ pl: { xs: 0, sm: 1.5 }, mt: { xs: 2, sm: 0 } }}
        >
          <Grid item xs={12} mb={0.5}>
            <Desc>รูปแบบการจัดส่ง</Desc>
          </Grid>

          <Grid item xs={12}>
            <InputText disabled value={isJSONString(info?.method)} />
          </Grid>
        </Grid>

        {info?.fulfillmentProcess.length > 1 ? (
          <>
            <Grid conaiter xs={12} mt={2}>
              <Desc primary>การส่งของที่ 1 (ร้านค้าส่งไปขนส่ง)</Desc>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              sx={{ mt: { xs: 2 }, pr: { xs: 0, sm: 1.5 } }}
            >
              <Grid item xs={12} mb={0.5}>
                <Desc>ขนส่งที่ใช้ในการจัดส่ง</Desc>
              </Grid>

              {info?.fulfillmentProcess[0]?.carrier === "อื่นๆ" ? (
                <Grid container xs={12}>
                  <Grid item xs={4} pr={1}>
                    <InputText
                      disabled
                      value={info?.fulfillmentProcess[0]?.carrier}
                    />
                  </Grid>

                  <Grid item xs={8} pl={1}>
                    <InputText
                      disabled
                      value={info?.fulfillmentProcess[0]?.carrierAdditional}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <InputText
                    disabled
                    value={info?.fulfillmentProcess[0]?.carrier}
                  />
                </Grid>
              )}
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              sx={{ mt: { xs: 2 }, pl: { xs: 0, sm: 1.5 } }}
            >
              <Grid item xs={12} mb={0.5}>
                <Desc>ค่าจัดส่ง</Desc>
              </Grid>

              <Grid item xs={12}>
                <InputText
                  disabled
                  value={
                    info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง"
                      ? "มีค่าจัดส่ง"
                      : info?.fulfillmentProcess[0]?.type === "ไม่มีค่าจัดส่ง"
                        ? "ไม่มีค่าจัดส่ง"
                        : "เก็บเงินปลายทาง"
                  }
                />
              </Grid>
            </Grid>

            {info?.fulfillmentProcess[0].carrier === "Lalamove" && (
              <Grid container xs={12} sx={{ mt: { xs: 2, sm: 1, md: 2 } }}>
                <Grid
                  container
                  xs={9.2}
                  sm={10.6}
                  md={10.8}
                  sx={{ pr: { xs: 1, sm: 2 } }}
                >
                  <Grid item xs={12}>
                    <Desc>Link ติดตามสินค้าของ Lalamove</Desc>
                  </Grid>
                  <Grid item xs={12} mt={1}>
                    <InputText
                      value={info?.fulfillmentProcess[0]?.trackingUrl}
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  xs={2.8}
                  sm={1.4}
                  md={1.2}
                  justifyContent="flex-end"
                  alignItems="flex-end"
                >
                  <Button
                    variant="outlined"
                    className="MuiButton-edit"
                    sx={{
                      height: {
                        xs: "40px !important",
                        md: "48px !important",
                      },
                    }}
                    onClick={() =>
                      window.open(
                        info?.fulfillmentProcess[0]?.trackingUrl,
                        "_blank",
                      )
                    }
                  >
                    เปิด Link
                  </Button>
                </Grid>
              </Grid>
            )}

            {info?.fulfillmentProcess[0].carrier === "อื่นๆ" && (
              <Grid container xs={12} sx={{ mt: { xs: 2, sm: 1, md: 2 } }}>
                <Grid container xs={12}>
                  <Grid item xs={12}>
                    <Desc>เลขพัสดุ</Desc>
                  </Grid>
                  <Grid item xs={12} mt={1}>
                    <InputText
                      value={info?.fulfillmentProcess[0]?.trackingNumber}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid conaiter xs={12} mt={2}>
              <Desc primary>การส่งของที่ 2 (ขนส่งส่งไปลูกค้า)</Desc>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              sx={{ mt: { xs: 2 }, pr: { xs: 0, sm: 1.5 } }}
            >
              <Grid item xs={12} mb={0.5}>
                <Desc>ขนส่งที่ใช้ในการจัดส่ง</Desc>
              </Grid>

              <Grid container xs={12}>
                <Grid item xs={4} pr={1}>
                  <InputText
                    disabled
                    value={info?.fulfillmentProcess[1]?.carrier}
                  />
                </Grid>

                <Grid item xs={8} pl={1}>
                  <InputText
                    disabled
                    value={info?.fulfillmentProcess[1]?.carrierAdditional}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              sx={{ mt: { xs: 2 }, pl: { xs: 0, sm: 1.5 } }}
            >
              <Grid item xs={12} mb={0.5}>
                <Desc>ค่าจัดส่ง</Desc>
              </Grid>

              <Grid item xs={12}>
                <InputText
                  disabled
                  value={
                    info?.fulfillmentProcess[1]?.type === "มีค่าจัดส่ง"
                      ? "มีค่าจัดส่ง"
                      : info?.fulfillmentProcess[1]?.type === "ไม่มีค่าจัดส่ง"
                        ? "ไม่มีค่าจัดส่ง"
                        : "เก็บเงินปลายทาง"
                  }
                />
              </Grid>
            </Grid>

            <Grid container xs={12} sx={{ mt: { xs: 2, sm: 1, md: 2 } }}>
              <Grid container xs={12}>
                <Grid item xs={12}>
                  <Desc>เลขพัสดุ</Desc>
                </Grid>
                <Grid item xs={12} mt={1}>
                  <InputText
                    value={info?.fulfillmentProcess[1]?.trackingNumber}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            {isJSONString(info?.method) !== "ลูกค้ารับเอง" ? (
              <>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ mt: { xs: 2 }, pr: { xs: 0, sm: 1.5 } }}
                >
                  <Grid item xs={12} mb={0.5}>
                    <Desc>ขนส่งที่ใช้ในการจัดส่ง</Desc>
                  </Grid>

                  {info?.fulfillmentProcess[0]?.carrier === "อื่นๆ" ? (
                    <Grid container xs={12}>
                      <Grid item xs={4} pr={1}>
                        <InputText
                          disabled
                          value={info?.fulfillmentProcess[0]?.carrier}
                        />
                      </Grid>

                      <Grid item xs={8} pl={1}>
                        <InputText
                          disabled
                          value={info?.fulfillmentProcess[0]?.carrierAdditional}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <InputText
                        disabled
                        value={info?.fulfillmentProcess[0]?.carrier}
                      />
                    </Grid>
                  )}
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ mt: { xs: 2 }, pl: { xs: 0, sm: 1.5 } }}
                >
                  <Grid item xs={12} mb={0.5}>
                    <Desc>ค่าจัดส่ง</Desc>
                  </Grid>

                  <Grid item xs={12}>
                    <InputText
                      disabled
                      // value={
                      //   info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง"
                      //     ? "มีค่าจัดส่ง"
                      //     : info?.fulfillmentProcess[0]?.type ===
                      //         "ไม่มีค่าจัดส่ง"
                      //       ? "ไม่มีค่าจัดส่ง"
                      //       : "เก็บเงินปลายทาง"
                      // }
                      value={info?.fulfillmentProcess[0]?.type}
                    />
                  </Grid>
                </Grid>

                {(info?.fulfillmentProcess[0]?.carrier === "Lalamove" ||
                  info?.fulfillmentProcess[0]?.carrier ===
                    "Lalamove ส่งฟรี") && (
                  <Grid container xs={12} sx={{ mt: { xs: 2, sm: 1, md: 2 } }}>
                    <Grid
                      container
                      xs={9.2}
                      sm={10.6}
                      md={10.8}
                      sx={{ pr: { xs: 1, sm: 2 } }}
                    >
                      <Grid item xs={12}>
                        <Desc>Link ติดตามสินค้าของ Lalamove</Desc>
                      </Grid>
                      <Grid item xs={12} mt={1}>
                        <InputText
                          value={info?.fulfillmentProcess[0]?.trackingUrl}
                        />
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      xs={2.8}
                      sm={1.4}
                      md={1.2}
                      justifyContent="flex-end"
                      alignItems="flex-end"
                    >
                      <Button
                        variant="outlined"
                        className="MuiButton-edit"
                        sx={{
                          height: {
                            xs: "40px !important",
                            md: "48px !important",
                          },
                        }}
                        onClick={() =>
                          window.open(
                            info?.fulfillmentProcess[0]?.trackingUrl,
                            "_blank",
                          )
                        }
                      >
                        เปิด Link
                      </Button>
                    </Grid>
                  </Grid>
                )}

                {info?.fulfillmentProcess[0]?.carrier === "อื่นๆ" && (
                  <Grid container xs={12} sx={{ mt: { xs: 2, sm: 1, md: 2 } }}>
                    <Grid container xs={12}>
                      <Grid item xs={12}>
                        <Desc>เลขพัสดุ</Desc>
                      </Grid>
                      <Grid item xs={12} mt={1}>
                        <InputText
                          value={info?.fulfillmentProcess[0]?.trackingNumber}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ pr: { xs: 0, sm: 1.5 } }}
                  mt={2}
                >
                  <Grid item xs={12} mb={0.5}>
                    <Desc>ที่อยู่ร้านค้า</Desc>
                  </Grid>

                  <Grid item xs={12}>
                    <InputText
                      disabled
                      value={
                        info?.fulfillmentProcess[0]?.pickupAddress?.address
                      }
                    />
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ pl: { xs: 0, sm: 1.5 } }}
                  mt={2}
                >
                  <Grid item xs={12} mb={0.5}>
                    <Desc>เบอร์ติดต่อ</Desc>
                  </Grid>

                  <Grid item xs={12}>
                    <InputText
                      disabled
                      value={info?.fulfillmentProcess[0]?.pickupAddress?.tel}
                    />
                  </Grid>
                </Grid>

                <Grid container xs={12} sx={{ mt: { xs: 2, sm: 1 } }}>
                  <Button
                    variant="outlined"
                    className="MuiButton-location"
                    onClick={() =>
                      openDialog({
                        type: "custom",
                        customDialog: (
                          <LocationDialog
                            info={info}
                            onClose={() => closeDialog()}
                          />
                        ),
                      })
                    }
                    width={"300px"}
                  >
                    <LocationOnIcon fontSize="small" />
                    ดูหมุด Location
                  </Button>

                  <Button
                    variant="outlined"
                    className="MuiButton-location-google-maps"
                    onClick={() =>
                      window.open(`https://www.google.com/maps?q=${info?.fulfillmentProcess[0]?.pickupAddress?.coordinates?.lat},${info?.fulfillmentProcess[0]?.pickupAddress?.coordinates?.lng}`, '_blank')
                    }
                  >
                    <LocationOnIcon fontSize="small" />
                    ดูหมุดบน Google Maps
                  </Button>
                </Grid>
              </>
            )}
          </>
        )}

        <Grid container xs={12} sx={{ mt: { xs: 2, sm: 1, md: 2 } }}>
          <Grid
            container
            xs={12}
            sm={6}
            sx={{ pr: { xs: 0, sm: 2 } }}
            justifyContent="space-between"
          >
            <Grid
              item
              xs={info?.method !== "ลูกค้ารับเอง" ? 5.9 : 12}
              sm={info?.method !== "ลูกค้ารับเอง" ? 5.75 : 12}
              sx={
                info?.method !== "ลูกค้ารับเอง"
                  ? { pr: { xs: 1, sm: 2 } }
                  : { pr: { xs: 0 } }
              }
            >
              <Grid item xs={12}>
                <Desc>วันที่จัดส่ง</Desc>
              </Grid>
              <Grid item xs={12} mt={1}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={
                      info?.fulfillmentProcess?.[0]?.deliveryProofs?.[0]
                        ?.deliveryProof?.createdAt
                    }
                    onChange={() => {}}
                    disabled
                    inputFormat="DD-MM-YYYY"
                    renderInput={(params) => (
                      <DateField
                        {...params}
                        defaultValue="2017-05-24"
                        InputProps={{
                          ...params.InputProps,
                          sx: {
                            "& svg": {
                              color: "#252525", // Change this to your desired color
                            },
                            height: {
                              xs: "40px !important",
                              md: "48px !important",
                            },
                          },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            {info?.method !== "ลูกค้ารับเอง" && (
              <>
                <Grid
                  item
                  xs={0.2}
                  sm={0.5}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="center"
                    sx={{ mt: { xs: 3.5, sm: 2.5, md: 4 } }}
                  >
                    <Desc>-</Desc>
                  </Grid>
                </Grid>

                <Grid item xs={5.9} sm={5.75} sx={{ pl: { xs: 1, sm: 2 } }}>
                  <Grid item xs={12}>
                    <Desc>วันที่คาดว่าจะถึง</Desc>
                  </Grid>
                  <Grid item xs={12} mt={1}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={
                          info?.fulfillmentProcess[0]?.estimatedDeliveryDate
                        }
                        onChange={(newValue) => {}}
                        disabled
                        inputFormat="DD-MM-YYYY"
                        renderInput={(params) => (
                          <DateField
                            {...params}
                            defaultValue="2017-05-24"
                            InputProps={{
                              ...params.InputProps,
                              sx: {
                                "& svg": {
                                  color: "#252525", // Change this to your desired color
                                },
                                height: {
                                  xs: "40px !important",
                                  md: "48px !important",
                                },
                                fontSize: {
                                  xs: "0.875rem !important",
                                  md: "1rem !important",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>

          <Grid
            container
            xs={12}
            sm={6}
            sx={{ mt: { xs: 2, sm: 0 }, pl: { xs: 0, sm: 2 } }}
          >
            <Grid item xs={12}>
              <Desc>รูปแพ็คสินค้าก่อนจัดส่ง</Desc>
            </Grid>

            {!!info?.fulfillmentProcess?.[0]?.deliveryProofs &&
            info?.fulfillmentProcess?.[0]?.deliveryProofs?.length > 0 ? (
              <Grid item xs={12} mt={1}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<VisibilityIcon />}
                  fullWidth
                  onClick={() => {
                    openDialog({
                      type: "custom",
                      customDialog: (
                        <PreviewImageList
                          onClose={() => closeDialog()}
                          title="รูปแพ็คสินค้าก่อนจัดส่ง"
                          imageList={
                            info.fulfillmentProcess?.[0]?.deliveryProofs
                          }
                          keyImg={"deliveryProof"}
                        />
                      ),
                    });
                  }}
                  sx={{
                    height: {
                      xs: "40px !important",
                      md: "48px !important",
                    },
                  }}
                >
                  ดูรูปสินค้าที่จัดส่ง
                </Button>
              </Grid>
            ) : (
              <Grid item xs={12} mt={1}>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={
                    !checkAllowPermission("orders.can_update_shippingOrder")
                  }
                  onClick={() => {
                    openDialog({
                      type: "custom",
                      customDialog: (
                        <DeliveryProofImgDialog
                          orderDetail={info}
                          onClose={() => closeDialog()}
                          onSuccess={() => {
                            closeAllDialogs();
                            callBackFetchInfo();
                          }}
                          setIsLoading={setIsLoading}
                        />
                      ),
                    });
                  }}
                  startIcon={<AddIcon />}
                  fullWidth
                  sx={{
                    height: {
                      xs: "40px !important",
                      md: "48px !important",
                    },
                  }}
                >
                  เพิ่มรูปสินค้าที่จัดส่ง
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Desc>หมายเหตุ</Desc>
          </Grid>

          <Grid item xs={12} sx={{ mt: { xs: 1, md: 2 } }}>
            <InputText
              disabled
              value={info?.fulfillmentProcess[0]?.remark ?? "-"}
            />
          </Grid>
        </Grid>
      </Section>

      {isJSONString(info?.method) !== "ลูกค้ารับเอง" && (
        <>
          {info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" ||
          info?.fulfillmentProcess[1]?.type === "มีค่าจัดส่ง" ? (
            <>
              <Grid container sx={{ mt: { xs: 2, md: 3 } }}>
                <Title>ช่องทางการชำระเงิน</Title>
              </Grid>

              <Section borderBottom>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ pr: { xs: 0, sm: 1.5 }, mt: { xs: 2, sx: 0 } }}
                >
                  <Grid item xs={12} mb={0.5}>
                    <Desc>ชื่อบัญชี</Desc>
                  </Grid>

                  <Grid item xs={12}>
                    <InputText
                      disabled
                      value={info?.payment?.info?.bankAccountName}
                    />
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ pl: { xs: 0, sm: 1.5 }, mt: { xs: 2, sx: 0 } }}
                >
                  <Grid item xs={12} mb={0.5}>
                    <Desc>เลขที่บัญชี</Desc>
                  </Grid>

                  <Grid item xs={12}>
                    <InputText disabled value={info?.payment?.info?.bankNo} />
                  </Grid>
                </Grid>
              </Section>

              <Grid container xs={12}>
                <Grid item xs={12} sx={{ mt: { xs: 2, md: 3 } }}>
                  <Title>ค่าจัดส่งเพิ่มเติม</Title>
                </Grid>

                {info?.fulfillmentProcess.length > 1 ? (
                  <>
                    {info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" && (
                      <Grid container xs={12} sx={{ mt: { xs: 1, md: 2 } }}>
                        <Grid item xs={9}>
                          <Desc>
                            ค่าจัดส่ง (การส่งของที่ 1 (ร้านค้าส่งไปขนส่ง))
                          </Desc>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          display="flex"
                          justifyContent="flex-end"
                        >
                          <Desc>{`${convertIntToCurrency(info?.fulfillmentProcess[0]?.shippingFee)} บาท`}</Desc>
                        </Grid>
                      </Grid>
                    )}

                    {info?.fulfillmentProcess[1]?.type === "มีค่าจัดส่ง" ||
                    (info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" &&
                      info?.fulfillmentProcess[1]?.type ===
                        "เก็บเงินปลายทาง") ? (
                      <Grid container xs={12} sx={{ mt: { xs: 1, md: 1 } }}>
                        <Grid item xs={9}>
                          <Desc>
                            ค่าจัดส่ง (การส่งของที่ 2 (ขนส่งส่งไปลูกค้า))
                          </Desc>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          display="flex"
                          justifyContent="flex-end"
                        >
                          <Desc>{`${convertIntToCurrency(info?.fulfillmentProcess[1]?.shippingFee)} บาท`}</Desc>
                        </Grid>
                      </Grid>
                    ) : null}

                    <Grid container xs={12} sx={{ mt: { xs: 1, md: 2 } }}>
                      <Grid
                        item
                        xs={6}
                        display="flex"
                        alignItems="flex-end"
                        sx={{ mb: { xs: 0, sm: 2 } }}
                      >
                        <Title>ยอดชำระ</Title>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        display="flex"
                        justifyContent="flex-end"
                        sx={{ alignItems: { xs: "baseline" } }}
                      >
                        {(info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" &&
                          info?.fulfillmentProcess[1]?.type ===
                            "มีค่าจัดส่ง") ||
                        (info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" &&
                          info?.fulfillmentProcess[1]?.type ===
                            "เก็บเงินปลายทาง") ? (
                          <Price>
                            {`${convertIntToCurrency(info?.fulfillmentProcess[0]?.shippingFee + info?.fulfillmentProcess[1]?.shippingFee)} `}
                          </Price>
                        ) : info?.fulfillmentProcess[0]?.type ===
                          "มีค่าจัดส่ง" ? (
                          <Price>
                            {`${convertIntToCurrency(info?.fulfillmentProcess[0]?.shippingFee)} `}
                          </Price>
                        ) : info?.fulfillmentProcess[1]?.type ===
                          "มีค่าจัดส่ง" ? (
                          <Price>
                            {`${convertIntToCurrency(info?.fulfillmentProcess[1]?.shippingFee)} `}
                          </Price>
                        ) : null}

                        <Desc>บาท</Desc>
                      </Grid>
                    </Grid>

                    {info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" &&
                      info?.fulfillmentProcess[1]?.type ===
                        "เก็บเงินปลายทาง" && (
                        <>
                          <Line />

                          <Grid container xs={12} sx={{ mt: { xs: 2, md: 0 } }}>
                            <Grid
                              item
                              xs={8}
                              display="flex"
                              justifyItems="flex-end"
                            >
                              <Title>
                                ค่าจัดส่งโดยประมาณ (เก็บเงินปลายทาง)
                              </Title>
                            </Grid>

                            <Grid
                              item
                              xs={4}
                              display="flex"
                              justifyContent="flex-end"
                            >
                              <Price>
                                {convertIntToCurrency(
                                  info?.fulfillmentProcess[1]?.shippingFee,
                                )}
                              </Price>

                              <Desc>บาท</Desc>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <TextSubTable>
                              *เพื่อแจ้งให้ลูกค้าเตรียมเงินและทราบค่าจัดส่งโดยประมาณ
                            </TextSubTable>
                          </Grid>
                        </>
                      )}
                  </>
                ) : (
                  <>
                    <Grid container xs={12} sx={{ mt: { xs: 1, md: 3 } }}>
                      <Grid item xs={6}>
                        <Desc>ค่าจัดส่ง</Desc>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        display="flex"
                        justifyContent="flex-end"
                      >
                        <Desc>{`${convertIntToCurrency(info?.fulfillmentProcess[0]?.shippingFee)} บาท`}</Desc>
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      xs={12}
                      sx={{ mt: { xs: 1, md: 2 }, mb: { xs: 2, sm: 3 } }}
                    >
                      <Grid item xs={6} display="flex" alignItems="flex-end">
                        <Title>ยอดชำระ</Title>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        display="flex"
                        justifyContent="flex-end"
                      >
                        <Price>
                          {`${convertIntToCurrency(info?.fulfillmentProcess[0]?.shippingFee)} `}
                        </Price>

                        <Desc>บาท</Desc>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>

              <Line />
            </>
          ) : info?.fulfillmentProcess[0]?.type === "ไม่มีค่าจัดส่ง" &&
            info?.fulfillmentProcess[1]?.type === "เก็บเงินปลายทาง" ? (
            <>
              <Grid
                container
                xs={12}
                sx={{
                  mt: { xs: 2, md: 3 },
                }}
              >
                <Grid item xs={8} display="flex" justifyItems="flex-end">
                  <Title>ค่าจัดส่งโดยประมาณ (เก็บเงินปลายทาง)</Title>
                </Grid>

                <Grid
                  item
                  xs={4}
                  display="flex"
                  justifyContent="flex-end"
                  sx={{ alignItems: { xs: "baseline" } }}
                >
                  <Price>
                    {convertIntToCurrency(
                      info?.fulfillmentProcess[1]?.shippingFee,
                    )}
                  </Price>

                  <Desc>บาท</Desc>
                </Grid>
              </Grid>

              <Grid
                item
                xs={12}
                sx={{
                  mb: { xs: 2, sm: 3 },
                }}
              >
                <TextSubTable>
                  *เพื่อแจ้งให้ลูกค้าเตรียมเงินและทราบค่าจัดส่งโดยประมาณ
                </TextSubTable>
              </Grid>

              <Line />
            </>
          ) : info?.fulfillmentProcess[0]?.type === "เก็บเงินปลายทาง" ? (
            <>
              <Grid
                container
                xs={12}
                sx={{
                  mt: { xs: 2, md: 3 },
                }}
              >
                <Grid item xs={8} display="flex" justifyItems="flex-end">
                  <Title>ค่าจัดส่งโดยประมาณ (เก็บเงินปลายทาง)</Title>
                </Grid>

                <Grid
                  item
                  xs={4}
                  display="flex"
                  justifyContent="flex-end"
                  sx={{ alignItems: { xs: "baseline" } }}
                >
                  <Price>
                    {convertIntToCurrency(
                      info?.fulfillmentProcess[0]?.shippingFee,
                    )}
                  </Price>

                  <Desc>บาท</Desc>
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{ mb: { xs: 2, sm: 3 } }}>
                <TextSubTable>
                  *เพื่อแจ้งให้ลูกค้าเตรียมเงินและทราบค่าจัดส่งโดยประมาณ
                </TextSubTable>
              </Grid>

              <Line />
            </>
          ) : info?.fulfillmentProcess[0]?.type === "โดย Xsurface" ? (
            <>
              <Grid container>
                <Grid item xs={12} sx={{ mt: { xs: 2, md: 3 } }}>
                  <Title>ค่าจัดส่งเพิ่มเติม</Title>
                </Grid>

                <Grid item xs={12} container sx={{ mt: { xs: 1, md: 2 } }}>
                  <Grid item xs={6}>
                    <Desc>ค่าจัดส่ง Lalamove</Desc>
                  </Grid>

                  {/* shipping fee from lalamove + 100 */}
                  <Grid item xs={6} display="flex" justifyContent="flex-end">
                    <Desc>{`${convertIntToCurrency(info?.fulfillmentProcess[0]?.lalamoveCheck?.result?.totalWithFee)} บาท`}</Desc>
                  </Grid>
                </Grid>

                {/* only admin can view additional payment amount  */}
                {isAdmin && (
                  <>
                    <Grid item xs={12} container sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Desc>ยอดชำระเพิ่มเติม</Desc>
                      </Grid>

                      {/* if 800 < (shipping fee from lalamove + 100) <= 820 = 0
                                          else if (shipping fee from lalamove + 100) > 820 = (shipping fee from lalamove + 100) - 800 */}
                      <Grid
                        item
                        xs={6}
                        display="flex"
                        justifyContent="flex-end"
                      >
                        <Desc>{`${convertIntToCurrency(info?.fulfillmentProcess[0]?.lalamoveCheck?.result?.totalWithDiscount)} บาท`}</Desc>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} container sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Desc>ภาษีมูลค่าเพิ่ม</Desc>
                      </Grid>

                      {/* vat 7% of totalWithDiscount */}
                      <Grid
                        item
                        xs={6}
                        display="flex"
                        justifyContent="flex-end"
                      >
                        <Desc>{`${convertIntToCurrency(info?.fulfillmentProcess[0]?.lalamoveCheck?.result?.shippingVat || 0)} บาท`}</Desc>
                      </Grid>
                    </Grid>

                    <Grid item container xs={12} sx={{ mt: { xs: 1, md: 2 } }}>
                      <Grid item xs={6} display="flex" alignItems="flex-end">
                        <Title>ยอดเรียกชำระเพิ่มเติม</Title>
                      </Grid>

                      {/* totalWithDiscount + vat 7% || totalWithDiscount (if not have in old version) */}
                      <Grid
                        item
                        xs={6}
                        display="flex"
                        justifyContent="flex-end"
                      >
                        <Price>
                          {`${convertIntToCurrency(info?.fulfillmentProcess[0]?.lalamoveCheck?.result?.totalWithDiscountWithVat || info?.fulfillmentProcess[0]?.lalamoveCheck?.result?.totalWithDiscount)} `}
                        </Price>

                        <Desc>บาท</Desc>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </>
          ) : (
            info?.fulfillmentProcess[0]?.type !== "เก็บเงินปลายทาง" &&
            info?.fulfillmentProcess[0]?.type !== "ไม่มีค่าจัดส่ง" && (
              <>
                <Grid
                  container
                  xs={12}
                  sx={{
                    mt: { xs: 2, md: 3 },
                  }}
                >
                  <Grid item xs={8} display="flex" justifyItems="flex-end">
                    <Title>ค่าจัดส่งโดยประมาณ (เก็บเงินปลายทาง)</Title>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    display="flex"
                    justifyContent="flex-end"
                    sx={{ alignItems: { xs: "baseline" } }}
                  >
                    <Price>
                      {convertIntToCurrency(
                        info?.fulfillmentProcess[1]?.shippingFee,
                      )}
                    </Price>

                    <Desc>บาท</Desc>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <TextSubTable>
                    *เพื่อแจ้งให้ลูกค้าเตรียมเงินและทราบค่าจัดส่งโดยประมาณ
                  </TextSubTable>
                </Grid>

                <Line />
              </>
            )
          )}
        </>
      )}

      {(info?.fulfillmentProcess?.[0]?.type === "มีค่าจัดส่ง" ||
        info?.fulfillmentProcess?.[1]?.type === "มีค่าจัดส่ง") &&
        info?.method !== "ลูกค้ารับเอง" && (
          <>
            <Grid container xs={12}>
              <Grid item xs={12} sx={{ mt: { xs: 2, sm: 2 } }}>
                <Title>การชำระเงิน</Title>
              </Grid>
              <Grid container xs={12} sx={{ mt: { xs: 1, sm: 2 } }}>
                <Grid item xs={6}>
                  <Desc>วันที่ชำระเงิน</Desc>
                </Grid>

                <Grid item xs={6} display="flex" justifyContent="flex-end">
                  <Desc>
                    {dayjs(info?.payment?.paymentDate).format(
                      "DD-MM-YYYY HH:mm",
                    )}
                  </Desc>
                </Grid>
              </Grid>
            </Grid>

            <Grid container xs={12} my={2} justifyContent="center">
              <PaymentImage src={info?.payment?.paymentProof?.docUrl} />
            </Grid>

            <Grid
              xs={4.5}
              sm={2.5}
              md={2}
              lg={2}
              container
              justifyContent="center"
              mx="auto"
              sx={{ mt: { md: 1, lg: 2 } }}
            >
              <Button
                variant="outlined"
                className="MuiButton-download"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={() => {
                  const fileUrl = info.payment?.paymentProof?.docUrl;
                  const link = document.createElement("a");
                  link.href = fileUrl;
                  link.setAttribute("target", "_blank"); // เปิดในแท็บใหม่
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                ดาวน์โหลด
              </Button>
            </Grid>
          </>
        )}
    </Grid>
  );
};

const LalamoveInfo = ({ info, setIsLoading, callBackFetchInfo }) => {
  return (
    <Grid container columnSpacing={{ xs: 2, sm: 4 }} rowSpacing={1}>
      <Grid item xs={8}>
        <Title>สถานะการจัดส่งสินค้า Lalamove</Title>
      </Grid>

      <Grid item xs={4} sx={{ textAlign: "right" }}>
        <Button
          variant="outlined"
          // sx={{
          //   height: {
          //     xs: "40px !important",
          //     md: "48px !important",
          //   },
          // }}
          onClick={() => callBackFetchInfo()}
        >
          อัปเดตสถานะ
        </Button>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Desc mb={8}>สถานะการขนส่ง</Desc>
        <InputText
          disabled
          value={lalamoveStatus(
            info?.fulfillmentProcess[0]?.lalamoveRequest?.result?.status || "-",
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Desc mb={8}>ประเภทรถขนส่ง</Desc>
        <WrapperDeliveryType>
          {!!info?.fulfillmentProcess[0]?.lalamoveRequest?.request
            ?.serviceType ? (
            <Stack gap={1} direction="row" alignItems="center">
              <Box
                sx={{
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "cover",
                    objectPosition: "center",
                    borderRadius: "8px",
                  }}
                  src={`https://xsurface-images.s3.ap-southeast-1.amazonaws.com/lalamove-icon/${info?.fulfillmentProcess[0]?.lalamoveRequest?.request?.serviceType}.png`}
                  alt={`${info?.fulfillmentProcess[0]?.lalamoveRequest?.request?.serviceType}-img`}
                />
              </Box>

              <SpanDeliverType>
                {serviceType(
                  info?.fulfillmentProcess[0]?.lalamoveRequest?.request
                    ?.serviceType,
                )}
              </SpanDeliverType>
            </Stack>
          ) : (
            <SpanDeliverType>-</SpanDeliverType>
          )}

          <Price small>
            {convertPrice(
              Number(
                info?.fulfillmentProcess[0]?.lalamoveRequest?.result
                  ?.priceBreakdown?.total,
              ) + 100,
            )}
          </Price>
        </WrapperDeliveryType>
      </Grid>

      <Grid item xs={9} sm={10}>
        <Desc mb={8}>Link ติดตามสินค้าของ Lalamove</Desc>
        <InputText value={info?.fulfillmentProcess[0]?.trackingUrl} />
      </Grid>

      <Grid
        item
        xs={3}
        sm={2}
        container
        justifyContent="flex-end"
        alignItems="flex-end"
      >
        <Button
          variant="outlined"
          color="secondary"
          sx={{
            height: {
              xs: "40px !important",
              md: "48px !important",
            },
          }}
          onClick={() =>
            window.open(info?.fulfillmentProcess[0]?.trackingUrl, "_blank")
          }
        >
          เปิด Link
        </Button>
      </Grid>

      {!!info?.fulfillmentProcess[0]?.lalamoveRequest?.result?.driverDetail && (
        <>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Title>รายละเอียดคนขับ</Title>
          </Grid>

          <Grid item xs={12}>
            <Desc mb={8}>ชื่อคนขับ</Desc>
            <InputText
              disabled
              value={
                info?.fulfillmentProcess[0]?.lalamoveRequest?.result
                  ?.driverDetail?.name || "-"
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Desc mb={8}>ป้ายทะเบียนรถ</Desc>
            <InputText
              disabled
              value={
                info?.fulfillmentProcess[0]?.lalamoveRequest?.result
                  ?.driverDetail?.plateNumber || "-"
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Desc mb={8}>เบอร์โทรคนขับ</Desc>
            <WrapperDeliveryType>
              <SpanTel
                href={`tel:${info?.fulfillmentProcess[0]?.lalamoveRequest?.result?.driverDetail?.phone?.replace(
                  "+66",
                  "0",
                )}`}
              >
                {info?.fulfillmentProcess[0]?.lalamoveRequest?.result?.driverDetail?.phone?.replace(
                  "+66",
                  0,
                ) || "-"}
              </SpanTel>
            </WrapperDeliveryType>
          </Grid>
        </>
      )}
    </Grid>
  );
};

const Delivering = ({ info, setIsLoading, callbackCloseDocument }) => {
  return (
    <>
      <Content>
        {!!info?.fulfillmentProcess[0]?.lalamoveRequest?.result && (
          <Panel>
            <LalamoveInfo
              info={info}
              setIsLoading={setIsLoading}
              callBackFetchInfo={callbackCloseDocument}
            />
          </Panel>
        )}

        <Panel>
          <CustomerInfo info={info} />
        </Panel>

        <Panel>
          <PurchaseInfo info={info.order} />
        </Panel>

        <Panel>
          <ShippingDetail
            info={info}
            callBackFetchInfo={callbackCloseDocument}
            setIsLoading={setIsLoading}
          />
        </Panel>

        <Panel>
          <DocumentInfo
            info={info}
            setIsLoading={setIsLoading}
            isCloseDialog={callbackCloseDocument}
          />
        </Panel>

        <Panel>
          <Note info={info} />
        </Panel>
      </Content>
    </>
  );
};

export default Delivering;
